import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import useExcelUpload from "../../../../utils/hooks/useExcelUpload";
import UploadExcelDialog from "../../../Schedules/utils/UploadExcelEnhanced";
import DeletionDialog from "../../../../library/Dialog/DeletionDialog";
import {
  dateOnly,
  getSelectionStatusConfig,
  hasYearAccess,
  matchingComputations,
  MAX_COMPANIES,
  TAX_COMPUTATION_TYPE,
} from "../domain/preparation";
import {
  EXTERNAL_COMPANY_SAMPLE_DATA,
  EXTERNAL_COMPANY_UPLOAD_INSTRUCTIONS,
  EXTERNAL_COMPANY_VALIDATION_SCHEMA,
} from "../importExport/externalCompanyUpload";
import CorporateGroupSaveDialog from "./CorporateGroupSaveDialog";
import IconButtonWithTooltip from "../../../utils/IconButtonWithTooltip";

export default function CompanySelectionStep({ workspace: w }) {
  const [query, setQuery] = useState("");
  const [choices, setChoices] = useState({});
  const [uploadFeedback, setUploadFeedback] = useState(null);
  const [groupFeedback, setGroupFeedback] = useState(null);
  const [saveGroupOpen, setSaveGroupOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const upload = useExcelUpload({
    fileName: "group-relief-external-companies.xlsx",
    instructions: EXTERNAL_COMPANY_UPLOAD_INSTRUCTIONS,
    sampleData: EXTERNAL_COMPANY_SAMPLE_DATA,
    validationSchema: EXTERNAL_COMPANY_VALIDATION_SCHEMA,
    onUpload: (rows) => {
      const result = w.importExternalCompanies(rows || []);
      const messages = [];
      if (result.added)
        messages.push(
          `${result.added} external compan${result.added === 1 ? "y" : "ies"} added`
        );
      if (result.replaced)
        messages.push(
          `${result.replaced} external compan${result.replaced === 1 ? "y" : "ies"} replaced`
        );
      const summary = messages.length
        ? `${messages.join("; ")} in this case.`
        : "No external companies were added or replaced.";
      setUploadFeedback({
        severity: result.errors.length ? "warning" : "success",
        summary,
        errors: result.errors,
      });
    },
  });
  const filtered = w.companies.filter((company) =>
    `${company.name} ${company.uen}`
      .toLowerCase()
      .includes(query.trim().toLowerCase())
  );
  const activeGroup = w.corporateGroups.find(
    (group) => group.id === w.activeCorporateGroupId
  );

  const handleGroupChange = (event) => {
    const groupId = event.target.value;
    setGroupFeedback(null);
    if (!groupId) {
      w.setActiveCorporateGroupId("");
      return;
    }
    const result = w.applyCorporateGroup(groupId);
    if (!result.ok) {
      setGroupFeedback({
        severity: "error",
        message: result.unavailable.join(" "),
      });
      return;
    }
    const pendingExternalCount = result.pendingExternalCount || 0;
    const groupName =
      w.corporateGroups.find((group) => group.id === groupId)?.name ||
      "the saved group";
    setGroupFeedback({
      severity:
        result.unavailable.length || pendingExternalCount
          ? "warning"
          : "success",
      message: result.unavailable.length
        ? `${result.selectedCount} companies selected. Check these members: ${result.unavailable.join("; ")}.`
        : pendingExternalCount
          ? `${result.selectedCount} companies selected from ${groupName}. Upload ${pendingExternalCount} matching external compan${pendingExternalCount === 1 ? "y" : "ies"} to complete the group.`
          : `${result.selectedCount} companies selected from ${groupName}.`,
    });
  };

  const handleDeleteGroup = async () => {
    const result = await w.removeCorporateGroup(groupToDelete.id);
    if (result.ok) {
      setGroupFeedback({
        severity: "success",
        message: `${groupToDelete.name} was deleted. The current company selection is unchanged.`,
      });
      setGroupToDelete(null);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent='space-between'
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant='h5' fontWeight={750} gutterBottom>
            Select participating companies
          </Typography>
          <Typography color='text.secondary'>
            Choose 2–15 companies with an accessible computation for YA{" "}
            {w.settings.yearOfAssessment} and the common FYE.
          </Typography>
        </Box>
        <Button
          variant='outlined'
          startIcon={<UploadFileOutlinedIcon />}
          onClick={upload.handleOpenDialog}
          sx={{
            display: { xs: "none", md: "inline-flex" },
            height: 40,
            whiteSpace: "nowrap",
          }}
        >
          Upload external companies
        </Button>
        <IconButtonWithTooltip
          icon={UploadFileOutlinedIcon}
          tooltipTitle='Upload external companies'
          onClick={upload.handleOpenDialog}
          size='small'
          sx={{
            display: { xs: "inline-flex", md: "none" },
            alignSelf: { xs: "flex-end", sm: "auto" },
            width: 40,
            height: 40,
          }}
        />
      </Stack>
      {uploadFeedback && (
        <Alert
          severity={uploadFeedback.severity}
          onClose={() => setUploadFeedback(null)}
          sx={{ mb: 2 }}
        >
          <Typography variant='body2' fontWeight={650}>
            {uploadFeedback.summary}
          </Typography>
          {uploadFeedback.errors.slice(0, 4).map((error) => (
            <Typography key={error} variant='body2'>
              {error}
            </Typography>
          ))}
          {uploadFeedback.errors.length > 4 && (
            <Typography variant='body2'>
              {uploadFeedback.errors.length - 4} more rows were skipped.
            </Typography>
          )}
        </Alert>
      )}
      {w.loadError && (
        <Alert
          severity='error'
          sx={{ mb: 2 }}
          action={<Button onClick={w.loadCompanies}>Retry</Button>}
        >
          {w.loadError}
        </Alert>
      )}
      {w.selectionIssues.length > 0 && !w.loading && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Some selected companies no longer match this case. Upload or Remove
          them below and choose a matching computation.
        </Alert>
      )}
      <Paper
        variant='outlined'
        sx={{
          mb: 2,
          p: 1.5,
          borderRadius: 2.5,
          bgcolor: "action.hover",
        }}
      >
        <Stack
          direction='row'
          flexWrap='wrap'
          spacing={1.5}
          alignItems='center'
        >
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            sx={{ minWidth: 165 }}
          >
            <BusinessOutlinedIcon color='primary' />
            <Box>
              <Typography variant='body2' fontWeight={750}>
                Corporate group
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Reuse a company list
              </Typography>
            </Box>
          </Stack>
          <TextField
            select
            size='small'
            label='Saved corporate group'
            value={w.activeCorporateGroupId}
            disabled={w.corporateGroupsLoading || w.loading}
            onChange={handleGroupChange}
            sx={{ flex: "1 1 260px", minWidth: 0 }}
          >
            <MenuItem value=''>Save current selection as a new group</MenuItem>
            {w.corporateGroups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name} ·{" "}
                {(group.companyIds?.length || 0) +
                  (group.externalMembers?.length || 0)}{" "}
                companies
              </MenuItem>
            ))}
          </TextField>
          <Stack direction='row' spacing={1} flexWrap='wrap'>
            <Button
              size='small'
              variant='contained'
              disableElevation
              startIcon={<SaveOutlinedIcon />}
              disabled={
                w.selected.length < 2 || Boolean(w.corporateGroupAction)
              }
              onClick={() => setSaveGroupOpen(true)}
              sx={{ whiteSpace: "nowrap" }}
            >
              {activeGroup ? "Update group" : "Save group"}
            </Button>
            {activeGroup && (
              <IconButtonWithTooltip
                icon={DeleteOutlineIcon}
                tooltipTitle='Delete Group'
                onClick={() => setGroupToDelete(activeGroup)}
                color='error'
                size='small'
                disabled={Boolean(w.corporateGroupAction)}
              />
            )}
          </Stack>
        </Stack>
      </Paper>
      {(w.corporateGroupError || groupFeedback) && (
        <Alert
          severity={w.corporateGroupError ? "error" : groupFeedback.severity}
          onClose={
            w.corporateGroupError ? undefined : () => setGroupFeedback(null)
          }
          action={
            w.corporateGroupError ? (
              <Button
                color='inherit'
                size='small'
                onClick={w.loadCorporateGroups}
              >
                Retry
              </Button>
            ) : undefined
          }
          sx={{ mb: 2 }}
        >
          {w.corporateGroupError || groupFeedback.message}
        </Alert>
      )}
      {w.selected.length > 0 && (
        <Stack direction='row' flexWrap='wrap' gap={1} sx={{ mb: 2 }}>
          {w.selected.map((draft) => (
            <Chip
              key={draft.id}
              label={draft.name}
              color={
                w.selectionIssues.includes(draft.id) ? "warning" : "default"
              }
              onDelete={() => w.chooseCompany({ c_id: draft.id }, null, false)}
            />
          ))}
        </Stack>
      )}
      <Paper variant='outlined' sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Stack direction='row' spacing={2} sx={{ p: 2 }}>
          <TextField
            size='small'
            fullWidth
            label='Search company name or UEN'
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            startIcon={<RefreshIcon />}
            disabled={w.loading}
            onClick={w.loadCompanies}
          >
            Refresh
          </Button>
        </Stack>
        {w.loading && (
          <LinearProgress aria-label='Loading company computations' />
        )}
        <TableContainer>
          <Table
            size='small'
            sx={{ minWidth: 760 }}
            aria-label='Available companies'
          >
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox' />
                <TableCell>Company</TableCell>
                <TableCell>Tax computation</TableCell>
                <TableCell>Availability</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((company) => {
                const entry = w.catalog[company.c_id];
                const matches = matchingComputations(
                  entry?.computations || [],
                  w.settings
                );
                const selected = w.selectedIds.includes(company.c_id);
                const candidate =
                  choices[company.c_id] ||
                  w.drafts[company.c_id]?.computation.tc_id;
                const tc =
                  matches.length === 1
                    ? matches[0]
                    : matches.find((item) => item.tc_id === candidate);
                const restricted = !hasYearAccess(
                  company,
                  w.settings.yearOfAssessment
                );
                const duplicate = w.selected.some(
                  (item) =>
                    item.id !== company.c_id &&
                    item.uen?.trim().toLowerCase() ===
                      company.uen?.trim().toLowerCase()
                );
                const statusConfig = getSelectionStatusConfig({
                  restricted,
                  entry,
                  matches,
                  tc,
                  duplicate,
                  company,
                  selectedIds: w.selectedIds,
                  selected,
                  maxCompanies: MAX_COMPANIES,
                });

                const available = Boolean(
                  tc &&
                  !restricted &&
                  !entry?.error &&
                  !duplicate &&
                  company.uen &&
                  (selected || w.selectedIds.length < MAX_COMPANIES)
                );
                return (
                  <TableRow key={company.c_id} selected={selected} hover>
                    <TableCell padding='checkbox'>
                      <Checkbox
                        checked={selected}
                        disabled={!selected && !available}
                        inputProps={{ "aria-label": `Select ${company.name}` }}
                        onChange={(event) =>
                          w.chooseCompany(company, tc, event.target.checked)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <Typography variant='body2' fontWeight={650}>
                          {company.name}
                        </Typography>
                        {company.isExternal && (
                          <Stack
                            direction='row'
                            spacing={0.25}
                            alignItems='center'
                          >
                            <Chip
                              label='EXTERNAL'
                              size='small'
                              color='secondary'
                              variant='outlined'
                              sx={{
                                height: 16,
                                fontSize: "0.65rem",
                                fontWeight: 750,
                              }}
                            />
                            <IconButtonWithTooltip
                              icon={DeleteOutlineIcon}
                              tooltipTitle='Remove external company'
                              color='error'
                              size='small'
                              onClick={() =>
                                w.removeExternalCompany(company.c_id)
                              }
                              sx={{ p: 0.25, fontSize: "0.65rem" }}
                            />
                          </Stack>
                        )}
                      </Stack>
                      <Typography variant='caption' color='text.secondary'>
                        {company.uen || "UEN missing"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {matches.length > 1 ? (
                        <TextField
                          select
                          size='small'
                          label='Source computation'
                          value={tc?.tc_id || ""}
                          disabled={selected}
                          sx={{ minWidth: 220 }}
                          onChange={(event) =>
                            setChoices((current) => ({
                              ...current,
                              [company.c_id]: event.target.value,
                            }))
                          }
                        >
                          {matches.map((item) => (
                            <MenuItem key={item.tc_id} value={item.tc_id}>
                              {item.taxComputationType || "Tax computation"} ·{" "}
                              {dateOnly(item.financialYearStart)} ·{" "}
                              {String(item.tc_id).slice(-6)}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : tc ? (
                        <>
                          <Typography variant='body2'>
                            YA {tc.yearOfAssessment} ·{" "}
                            {dateOnly(tc.financialYearEnd)}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {TAX_COMPUTATION_TYPE[tc.taxComputationType] ||
                              "Tax computation"}{" "}
                            · {tc.currency || "Currency unspecified"}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant='body2' color='text.secondary'>
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction='row' alignItems='center' spacing={1}>
                        <Chip
                          size='small'
                          variant={
                            selected && statusConfig.color === "success"
                              ? "filled"
                              : "outlined"
                          }
                          color={statusConfig.color}
                          icon={
                            statusConfig.loading ? (
                              <CircularProgress
                                size={13}
                                thickness={5}
                                color='inherit'
                              />
                            ) : (
                              statusConfig.icon
                            )
                          }
                          label={statusConfig.label}
                          sx={(theme) => ({
                            fontWeight:
                              selected && statusConfig.color === "success"
                                ? 700
                                : 500,

                            "& .MuiChip-icon": {
                              fontSize: 16,
                            },

                            ...(statusConfig.loading && {
                              color: theme.palette.info.main,
                              borderColor: alpha(theme.palette.info.main, 0.3),

                              animation:
                                "loadingPulse 1.8s ease-in-out infinite",

                              "@keyframes loadingPulse": {
                                "0%, 100%": {
                                  backgroundColor: alpha(
                                    theme.palette.info.main,
                                    0.04
                                  ),
                                },
                                "50%": {
                                  backgroundColor: alpha(
                                    theme.palette.info.main,
                                    0.12
                                  ),
                                },
                              },

                              "& .MuiCircularProgress-root": {
                                color: "inherit",
                              },
                            }),
                          })}
                        />
                        {statusConfig.action === "retry" && (
                          <Button
                            size='small'
                            disabled={w.loading || Boolean(entry?.loading)}
                            onClick={() => w.retryCompany(company)}
                            sx={{ minWidth: "auto" }}
                          >
                            Retry
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {!filtered.length && !w.loading && (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Typography fontWeight={650}>
              {query
                ? "No companies match your search"
                : "No accessible companies found"}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {query
                ? "Try another company name or UEN."
                : "Companies available to your account will appear here."}
            </Typography>
          </Box>
        )}
      </Paper>
      <UploadExcelDialog
        title='External Group Relief Companies'
        open={upload.openDialog}
        onClose={upload.handleCloseDialog}
        onDownloadTemplate={upload.handleDownloadTemplate}
        onFileChange={upload.handleFileChange}
        onUpload={upload.handleUpload}
        selectedFile={upload.selectedFile}
        uploading={upload.uploading}
        alertMessage={upload.alertMessage}
        alertSeverity={upload.alertSeverity}
        onCloseAlert={upload.handleCloseAlert}
        onReset={upload.handleResetFile}
        errorFileUrl={upload.errorFileUrl}
        validRows={upload.rowCount}
        templateHeadersString={upload.getTemplateHeadersString()}
        templateInstructions={EXTERNAL_COMPANY_UPLOAD_INSTRUCTIONS}
        fixedValueGroups={[
          { label: "Company Type", values: ["Trading", "Investment"] },
          { label: "Qualifies for SUTE", values: ["TRUE", "FALSE"] },
        ]}
        infoTitle='External companies are case-only'
        infoMessage='They are available only in this Group Relief case and are not added to the your existing company list.'
      />
      <CorporateGroupSaveDialog
        open={saveGroupOpen}
        group={activeGroup}
        selected={w.selected}
        saving={w.corporateGroupAction === "save"}
        onSave={w.saveCorporateGroup}
        onClose={(savedGroup) => {
          setSaveGroupOpen(false);
          if (savedGroup?.name)
            setGroupFeedback({
              severity: "success",
              message: `${savedGroup.name} was saved.`,
            });
        }}
      />
      <DeletionDialog
        open={Boolean(groupToDelete)}
        onClose={() => setGroupToDelete(null)}
        title='Delete corporate group?'
        count={1}
        itemSingular='saved corporate group'
        previewTitles={groupToDelete ? [groupToDelete.name] : []}
        warning={
          <Stack spacing={1}>
            {w.corporateGroupError && (
              <Alert severity='error'>{w.corporateGroupError}</Alert>
            )}
            <Alert severity='info'>
              This removes the saved company list only. It does not delete any
              company or change the current selection.
            </Alert>
          </Stack>
        }
        deleting={w.corporateGroupAction === "delete"}
        onConfirm={handleDeleteGroup}
      />
    </Box>
  );
}
